import React, { useEffect, useRef, useState } from 'react';
import { Fab } from '@mui/material';
import { SupportAgent as SupportAgentIcon } from '../icons';
import { useSupportAssistant } from './SupportAssistantContext';
import supportFaqData from './supportFaqData';
import { T } from './Translate';
import { useNavigate } from 'react-router-dom';
import AiLinkButtons from '../components/AiLinkButtons';

// Asistente flotante de toda la app (lo monta AppRoutes en App.js): burbuja
// fija en la esquina inferior derecha que, al abrirse, muestra un menú de
// categorías de FAQ (supportFaqData) o el chat con la IA (Qwen, ver
// SupportAssistantContext). La IA puede devolver links a publicaciones y
// derivar al chat de soporte con una persona (/support/chat).
const SupportAssistantWidget = () => {
  const { isOpen, openAssistant, closeAssistant, messages, sendMessage, loading } = useSupportAssistant();
  const [view, setView] = useState('menu'); // 'menu' | 'category' | 'chat'
  const [activeCategory, setActiveCategory] = useState(null);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (view === 'chat' && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, view]);

  const headerTitle = view === 'menu' && activeCategory ? activeCategory.label : <T>Asistente Notiline</T>;

  const toggleOpen = () => {
    if (isOpen) {
      closeAssistant();
    } else {
      openAssistant();
    }
  };

  const openCategory = (category) => {
    setActiveCategory(category);
    setView('category');
  };

  const backToMenu = () => {
    setActiveCategory(null);
    setView('menu');
  };

  const startChat = () => {
    setView('chat');
  };

  const handleSend = (event) => {
    event.preventDefault();
    if (!input.trim()) return;
    sendMessage(input.trim());
    setInput('');
  };

  if (!isOpen) {
    return (
      <Fab
        onClick={toggleOpen}
        sx={{
          position: 'fixed',
          bottom: '70px',
          right: '16px',
          backgroundColor: '#27ae60',
          color: '#fff',
          boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
          zIndex: 1300,
          '&:hover': { backgroundColor: '#1f8b4d' },
        }}
        aria-label="Abrir asistente de soporte"
      >
        <SupportAgentIcon />
      </Fab>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '90px',
        right: '16px',
        width: '340px',
        maxWidth: 'calc(100vw - 32px)',
        height: '480px',
        maxHeight: 'calc(100vh - 114px)',
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        zIndex: 1300,
      }}
    >
      <div
        style={{
          backgroundColor: '#27ae60',
          color: '#fff',
          padding: '0.75rem 1rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <div style={{ fontWeight: 600 }}>{headerTitle}</div>
          <div style={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
            <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#a3f7bf', display: 'inline-block' }} />
            <T>En línea</T>
          </div>
        </div>
        <button
          onClick={toggleOpen}
          style={{ background: 'none', border: 'none', color: '#fff', fontSize: '1.2rem', cursor: 'pointer' }}
          aria-label="Cerrar asistente"
        >
          ✕
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '1rem' }}>
        {view === 'menu' && (
          <>
            <p style={{ margin: '0 0 0.4rem', color: '#555', fontSize: '0.88rem' }}>
              <T>Hola, soy el asistente de Notiline. Elegí una categoría para ver soluciones rápidas o escribime lo que necesites:</T>
            </p>
            {supportFaqData.map((category) => (
              <button
                key={category.id}
                onClick={() => openCategory(category)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  width: '100%',
                  textAlign: 'left',
                  padding: '0.6rem 0.5rem',
                  border: 'none',
                  borderBottom: '1px solid #eee',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                }}
              >
                <span>{category.icon}</span>
                <span>{category.label}</span>
              </button>
            ))}
            <button
              onClick={startChat}
              style={{
                width: '100%',
                marginTop: '0.75rem',
                padding: '0.6rem',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#27ae60',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '0.85rem',
              }}
            >
              <T>💬 Preguntarle al asistente</T>
            </button>
          </>
        )}

        {view === 'category' && activeCategory && (
          <>
            <button
              onClick={backToMenu}
              style={{ background: 'none', border: 'none', color: '#27ae60', cursor: 'pointer', marginBottom: '0.5rem', padding: 0 }}
            >
              ← <T>Volver</T>
            </button>
            {activeCategory.questions.map((item, idx) => {
              return (
                <div
                  key={idx}
                  style={{ marginBottom: '0.75rem' }}
                >
                  <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#2980b9', marginBottom: '0.2rem' }}>{item.q}</div>
                  <div style={{ fontSize: '0.85rem', color: '#555' }}>{item.a}</div>
                </div>
              );
            })}
            <button
              onClick={startChat}
              style={{
                width: '100%',
                marginTop: '0.5rem',
                padding: '0.6rem',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#27ae60',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '0.85rem',
              }}
            >
              <T>💬 Preguntarle al asistente</T>
            </button>
          </>
        )}

        {view === 'chat' && (
          <>
            {messages.map((m, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
                  marginBottom: '0.5rem',
                }}
              >
                <div
                  style={{
                    maxWidth: m.links?.length ? '95%' : '80%',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '10px',
                    backgroundColor: m.role === 'user' ? '#27ae60' : '#f1f1f1',
                    color: m.role === 'user' ? '#fff' : '#333',
                    fontSize: '0.85rem',
                  }}
                >
                  {m.text}
                  <AiLinkButtons links={m.links} onNavigate={closeAssistant} tone="light" />
                  {m.handoff && (
                    <button
                      onClick={() => {
                        closeAssistant();
                        navigate('/support/chat');
                      }}
                      style={{
                        marginTop: '0.4rem',
                        padding: '0.35rem 0.6rem',
                        borderRadius: '6px',
                        border: 'none',
                        backgroundColor: '#27ae60',
                        color: '#fff',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                      }}
                    >
                      <T>Hablar con una persona</T>
                    </button>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ fontSize: '0.8rem', color: '#999', fontStyle: 'italic' }}>
                <T>Pensando...</T>
              </div>
            )}
            {messages.length > 1 && !loading && !messages[messages.length - 1].handoff && (
              <button
                onClick={() => {
                  closeAssistant();
                  navigate('/support/chat');
                }}
                style={{ background: 'none', border: 'none', color: '#27ae60', cursor: 'pointer', fontSize: '0.8rem', padding: 0, margin: '0.25rem 0' }}
              >
                <T>¿No se resolvió? Hablar con una persona</T> →
              </button>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {view === 'chat' && (
        <form onSubmit={handleSend} style={{ display: 'flex', borderTop: '1px solid #eee', padding: '0.5rem' }}>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Escribí tu consulta..."
            style={{
              flex: 1,
              border: '1px solid #ddd',
              borderRadius: '8px',
              padding: '0.5rem',
              fontSize: '0.85rem',
              marginRight: '0.5rem',
            }}
          />
          <button
            type="submit"
            style={{
              backgroundColor: '#27ae60',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              padding: '0 0.9rem',
              cursor: 'pointer',
            }}
          >
            ➤
          </button>
        </form>
      )}
    </div>
  );
};

export default SupportAssistantWidget;
